import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Check, Search } from "lucide-react";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFixedFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/button";
import { cn } from "utils/cn";
import { useAddTenantAdmin } from "@/hooks/useAddTenantAdmin";
import {
  useSearchUsersByEmail,
  type UserSearchResult,
} from "@/hooks/useSearchUsersByEmail";

interface AddAdminDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  tenantId: string;
  tenantName: string;
  existingAdminUids: string[];
}

type SearchState =
  | { kind: "idle" }
  | { kind: "searching" }
  | { kind: "results"; users: UserSearchResult[] }
  | { kind: "error" };

export function AddAdminDialog({
  open,
  onOpenChange,
  tenantId,
  tenantName,
  existingAdminUids,
}: AddAdminDialogProps) {
  const { t } = useTranslation(["admin"]);
  const { searchUsers, isSearching } = useSearchUsersByEmail();
  const { addAdmin, isSubmitting } = useAddTenantAdmin(tenantId, tenantName);

  const [emailInput, setEmailInput] = useState("");
  const [searchState, setSearchState] = useState<SearchState>({ kind: "idle" });
  const [selectedUid, setSelectedUid] = useState<string | null>(null);

  useEffect(() => {
    if (!open) {
      setEmailInput("");
      setSearchState({ kind: "idle" });
      setSelectedUid(null);
    }
  }, [open]);

  const handleSearch = async () => {
    const trimmed = emailInput.trim();
    if (trimmed.length === 0) return;

    setSearchState({ kind: "searching" });
    setSelectedUid(null);

    const result = await searchUsers(trimmed);
    if (!result.ok) {
      setSearchState({ kind: "error" });
      return;
    }

    const filtered = result.users.filter(
      (u) => !existingAdminUids.includes(u.uid),
    );
    setSearchState({ kind: "results", users: filtered });
  };

  const handleConfirm = async () => {
    if (selectedUid === null || searchState.kind !== "results") return;
    const selected = searchState.users.find((u) => u.uid === selectedUid);
    if (!selected) return;

    const success = await addAdmin(selected.uid, selected.email);
    if (success) onOpenChange(false);
  };

  const isConfirmDisabled = selectedUid === null || isSubmitting || isSearching;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-h-[85vh] w-[28rem] max-w-[90vw]"
        hideClose={isSubmitting}
        onInteractOutside={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
        onEscapeKeyDown={(e) => {
          if (isSubmitting) e.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>
            {t("admin:tenants.detail.addAdminDialog.title")}
          </DialogTitle>
          <DialogDescription>
            {t("admin:tenants.detail.addAdminDialog.description")}
          </DialogDescription>
        </DialogHeader>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            handleSearch();
          }}
          noValidate
          className="flex items-center gap-2"
        >
          <Input
            type="text"
            value={emailInput}
            onChange={(e) => setEmailInput(e.target.value)}
            placeholder={t(
              "admin:tenants.detail.addAdminDialog.searchPlaceholder",
            )}
            disabled={isSubmitting}
            testId="add-admin-search-input"
            aria-label={t("admin:tenants.detail.addAdminDialog.title")}
          />
          <Button
            type="submit"
            variant="outline"
            icon={<Search className="h-4 w-4" />}
            loading={isSearching}
            disabled={
              isSubmitting || isSearching || emailInput.trim().length === 0
            }
            testId="add-admin-search-button"
          >
            {t("admin:tenants.detail.addAdminDialog.searchButton")}
          </Button>
        </form>

        <div className="min-h-[3rem]" data-testid="add-admin-results">
          {searchState.kind === "results" && searchState.users.length === 0 && (
            <p
              className="text-sm text-muted-foreground"
              data-testid="add-admin-empty-results"
            >
              {t("admin:tenants.detail.addAdminDialog.searchEmptyResult")}
            </p>
          )}
          {searchState.kind === "error" && (
            <p
              className="text-sm text-destructive"
              role="alert"
              data-testid="add-admin-search-error"
            >
              {t("admin:tenants.detail.addAdminDialog.searchError")}
            </p>
          )}
          {searchState.kind === "results" && searchState.users.length > 0 && (
            <div className="overflow-hidden rounded-lg border border-border">
              <ul
                className="flex max-h-60 flex-col divide-y divide-border overflow-y-auto"
                role="listbox"
                aria-label={t("admin:tenants.detail.addAdminDialog.title")}
              >
                {searchState.users.map((user) => {
                  const isSelected = selectedUid === user.uid;
                  return (
                    <li key={user.uid}>
                      <button
                        type="button"
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => setSelectedUid(user.uid)}
                        disabled={isSubmitting}
                        className={cn(
                          "flex w-full items-center justify-between gap-2 px-3 py-2 text-left text-sm transition-colors",
                          isSelected
                            ? "bg-primary/10 text-primary"
                            : "hover:bg-accent hover:text-accent-foreground",
                        )}
                        data-testid={`add-admin-result-${user.uid}`}
                      >
                        <span className="truncate">{user.email}</span>
                        {isSelected && (
                          <Check
                            className="h-4 w-4 shrink-0"
                            aria-hidden="true"
                          />
                        )}
                      </button>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>

        <DialogFixedFooter>
          <DialogClose asChild>
            <Button
              variant="outline"
              disabled={isSubmitting}
              testId="add-admin-cancel"
            >
              {t("admin:tenants.detail.addAdminDialog.cancel")}
            </Button>
          </DialogClose>
          <Button
            type="button"
            onClick={handleConfirm}
            loading={isSubmitting}
            disabled={isConfirmDisabled}
            testId="add-admin-confirm"
          >
            {isSubmitting
              ? t("admin:tenants.detail.addAdminDialog.submitting")
              : t("admin:tenants.detail.addAdminDialog.confirm")}
          </Button>
        </DialogFixedFooter>
      </DialogContent>
    </Dialog>
  );
}
